import React from 'react';
import { NameResult } from '../types/NameResult';
import { FiX } from 'react-icons/fi';
import {
  HiOutlineLightBulb,
  HiOutlineTag,
  HiOutlineCheckCircle,
  HiOutlineScale,
  HiOutlineStar,
  HiOutlineHeart,
  HiOutlineGlobeAlt,
  HiOutlineCode,
} from 'react-icons/hi';

interface NameScoreModalProps {
  result: NameResult;
  onClose: () => void;
}

// Category icon mapping with react-icons
const CATEGORY_ICONS: Record<string, React.ReactElement> = {
  brandability: <HiOutlineLightBulb className="w-8 h-8" />,
  relevance: <HiOutlineTag className="w-8 h-8" />,
  availability: <HiOutlineCheckCircle className="w-8 h-8" />,
  neutrality: <HiOutlineScale className="w-8 h-8" />,
  differentiation: <HiOutlineStar className="w-8 h-8" />,
  feel: <HiOutlineHeart className="w-8 h-8" />,
  internationality: <HiOutlineGlobeAlt className="w-8 h-8" />,
  technical_aesthetics: <HiOutlineCode className="w-8 h-8" />,
};

// Category display names
const CATEGORY_NAMES: Record<string, string> = {
  brandability: 'Brandability',
  relevance: 'Relevance',
  availability: 'Availability',
  neutrality: 'Neutrality',
  differentiation: 'Differentiation',
  feel: 'Feel & Tone',
  internationality: 'International Appeal',
  technical_aesthetics: 'Technical Aesthetics',
};

// Score color utility
const getScoreColor = (score: number): string => {
  if (score >= 4) return 'bg-green-500';
  if (score === 3) return 'bg-yellow-400';
  return 'bg-red-400';
};

const getScoreBadgeColor = (score: number): string => {
  if (score >= 4) return 'bg-green-100 text-green-700 border-green-200';
  if (score === 3) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

export const NameScoreModal: React.FC<NameScoreModalProps> = ({ result, onClose }) => {
  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!result.score) return null;

  const { score } = result;

  // Close modal on backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black bg-opacity-50 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="bg-white md:rounded-2xl shadow-2xl max-w-4xl w-full h-full md:h-auto md:max-h-[90vh] overflow-y-auto animate-scaleIn">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 md:px-6 py-6 md:rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
            aria-label="Close"
          >
            <FiX className="w-6 h-6" />
          </button>
          
          <div className="pr-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 break-words">{result.name}</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="text-4xl font-bold">{score.total_score}</div>
                <div className="text-sm opacity-90">/ 40</div>
              </div>
              <span
                className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide ${
                  score.verdict === 'strong'
                    ? 'bg-green-400 text-green-900'
                    : score.verdict === 'moderate'
                    ? 'bg-yellow-400 text-yellow-900'
                    : 'bg-gray-300 text-gray-800'
                }`}
              >
                {score.verdict}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {/* Overall Assessment */}
          {result.description && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-gray-700">{result.description}</p>
            </div>
          )}

          {/* Category Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(score.scores).map(([key, value]) => (
              <div
                key={key}
                className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-blue-600">{CATEGORY_ICONS[key]}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {CATEGORY_NAMES[key]}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-bold border ${getScoreBadgeColor(
                          value
                        )}`}
                      >
                        {value}/5
                      </span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getScoreColor(
                        value
                      )}`}
                      style={{ width: `${(value / 5) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Rationale */}
                <p className="text-sm text-gray-600 leading-relaxed">
                  {score.rationale[key as keyof typeof score.rationale]}
                </p>
              </div>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition shadow-md hover:shadow-lg"
            >
              Close Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

